import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { Radio } from '@creditornot/cb-radio';
import React from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import { Typography as T } from 'common/typography';

import NavigationBar from './NavigationBar';
import StatusTable from './StatusTable';
import { FileMatch, MatchIdentifier } from '../types';

export type Props = {
  results: FileMatch[] | null;
  state: 'matching' | 'idle' | 'loading' | 'success' | 'error' | 'reading-menu';
  menuError: any;
  matchBy: MatchIdentifier;
  onMatchBy: (matchBy: MatchIdentifier) => void;
  onFileMatching: (files: File[]) => void;
  onFileUpload: () => void;
  onBackButtonClick: () => void;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

const Content = styled.div`
  margin: auto;
  margin-block-start: 32px;
  max-width: 800px;
`;

const SpinnerContainer = styled.div`
  text-align: center;
`;

const Title = styled(T.Heading4)`
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 32px;
`;

const RadioGroup = styled.div`
  margin: 8px 0 16px;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-inline-start: 24px;
  }
`;

const FileDrop = styled.div<{ isLoading?: boolean }>`
  ${typographyCss.Body3};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 88px;
  border: 2px dashed ${({ isLoading }) => (isLoading ? colors.wolt100 : cbSemanticCssVars.borderPrimary)};
  border-radius: 8px;
  outline: none;
`;

const FileSelectLink = styled(T.Body3)`
  cursor: pointer;
  font-weight: 500;
  color: ${colors.wolt100};
`;

const FileSpecs = styled.div`
  ${typographyCss.Small};
  margin-block-start: 4px;
  color: ${cbSemanticCssVars.textSecondary};
`;

const UploadButtonContainer = styled.div`
  width: 100%;
  margin-block-start: 64px;
  display: flex;
  justify-content: center;
`;

const StyledStatusTable = styled(StatusTable)`
  margin-block-start: 16px;
`;

const BulkImageUploadPage = ({
  results,
  state,
  menuError,
  matchBy,
  onMatchBy,
  onFileMatching,
  onFileUpload,
  onBackButtonClick,
}: Props) => {
  return (
    <Root>
      <NavigationBar onBackButtonClick={onBackButtonClick} />
      <ContentWrapper>
        <Content>
          {state === 'reading-menu' && (
            <SpinnerContainer>
              <Spinner size="large" />
            </SpinnerContainer>
          )}
          {menuError && <APIErrorNotification error={menuError} />}
          {state !== 'reading-menu' && !menuError && (
            <>
              <Title>
                <FormattedMessage id="menu.bulk-image.import-item-images" />
              </Title>
              <T.Body3>
                <FormattedMessage id="menu.bulk-image.identifier-selector-label" />
              </T.Body3>
              <RadioGroup>
                <StyledRadio
                  checked={matchBy === 'id'}
                  onChange={() => onMatchBy('id')}
                  disabled={!!results || state === 'matching'}
                >
                  <FormattedMessage id="menu.bulk-image.identifier-item-id" />
                </StyledRadio>
                <StyledRadio
                  checked={matchBy === 'barcode_gtin'}
                  onChange={() => onMatchBy('barcode_gtin')}
                  disabled={!!results || state === 'matching'}
                >
                  <FormattedMessage id="menu.bulk-image.identifier-item-gtin" />
                </StyledRadio>
                <StyledRadio
                  checked={matchBy === 'merchant_sku'}
                  onChange={() => onMatchBy('merchant_sku')}
                  disabled={!!results || state === 'matching'}
                >
                  <FormattedMessage id="menu.bulk-image.identifier-item-sku" />
                </StyledRadio>
              </RadioGroup>
              {(!results || state === 'matching') && (
                <Dropzone onDrop={onFileMatching} useFsAccessApi={false}>
                  {({ getRootProps, getInputProps }) => (
                    <FileDrop {...getRootProps()} isLoading={state === 'matching'}>
                      <input {...getInputProps()} />
                      {state === 'matching' ? (
                        <Spinner size="large" />
                      ) : (
                        <div>
                          <FormattedMessage
                            id="menu.bulk-image.choose-or-drop"
                            values={{ action: chunks => <FileSelectLink>{chunks}</FileSelectLink> }}
                          />
                          <FileSpecs>
                            <FormattedMessage id="menu.bulk-image.file-specs" />
                          </FileSpecs>
                        </div>
                      )}
                    </FileDrop>
                  )}
                </Dropzone>
              )}
              {!!results && (
                <>
                  {state === 'loading' && (
                    <Alert variant="info">
                      <FormattedMessage
                        id="menu.bulk-image.upload-info"
                        values={{ count: results.filter(r => r.status === 'success').length }}
                      />
                    </Alert>
                  )}
                  {state === 'success' && (
                    <Alert variant="success">
                      <FormattedMessage id="menu.bulk-image.upload-completed" />
                    </Alert>
                  )}
                  {state === 'idle' && (
                    <Alert variant="success">
                      <FormattedMessage id="menu.bulk-image.match-completed" />
                    </Alert>
                  )}
                  {state === 'error' && (
                    <Alert variant="warning">
                      <FormattedMessage id="menu.bulk-image.upload-errors" />
                    </Alert>
                  )}
                  {(state === 'idle' || state === 'loading') && (
                    <UploadButtonContainer>
                      <Button
                        variant="blue"
                        size="large"
                        onClick={onFileUpload}
                        disabled={state === 'loading' || results.filter(r => r.status === 'success').length === 0}
                        loading={state === 'loading'}
                      >
                        <FormattedMessage id="menu.bulk-image.start-upload" />
                      </Button>
                    </UploadButtonContainer>
                  )}
                  <StyledStatusTable data={results} />
                </>
              )}
            </>
          )}
        </Content>
      </ContentWrapper>
    </Root>
  );
};

export default BulkImageUploadPage;
